import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useCentros } from "../../hooks/useCentros";
import { useFormatos } from "../../hooks/useFormatos";

type Props = {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  initial?: {
    cuadroId: number;
    fecha: string; // YYYY-MM-DD
    horaInicio?: string; // HH:mm
    horaFin?: string; // HH:mm
    formatoId?: number; // 1/2/...
    centroId?: number | null;
    capacidad?: number;
    nota?: string;
  };
  onSubmit: (dto: any) => Promise<any>;
};

export default function TurnoFormDialog({ open, onClose, mode, initial, onSubmit }: Props) {
  const { rows: centros = [] } = useCentros();
  const { rows: formatos = [] } = useFormatos();

  const PRESENCIAL_ID = useMemo(() => {
    const f = formatos.find((x: any) => (x.nombreFormato ?? "").toLowerCase() === "presencial");
    return f?.id ?? 2; // fallback a 2 (tu cat actual)
  }, [formatos]);

  const [form, setForm] = useState({
    cuadroId: initial?.cuadroId ?? 0,
    fecha: initial?.fecha ?? dayjs().format("YYYY-MM-DD"),
    horaInicio: initial?.horaInicio ?? "09:00",
    horaFin: initial?.horaFin ?? "13:00",
    formatoId: initial?.formatoId ?? 1,
    centroId: (initial?.centroId ?? null) as number | null,
    capacidad: initial?.capacidad ?? 1,
    nota: initial?.nota ?? "",
    publicar: mode === "create" ? true : false,
  });

  const [errors, setErrors] = useState<{
    centroId?: string;
    horaInicio?: string;
    horaFin?: string;
  }>({});

  useEffect(() => {
    if (!open) return;
    setForm({
      cuadroId: initial?.cuadroId ?? 0,
      fecha: initial?.fecha ?? dayjs().format("YYYY-MM-DD"),
      horaInicio: initial?.horaInicio ?? "09:00",
      horaFin: initial?.horaFin ?? "13:00",
      formatoId: initial?.formatoId ?? 1,
      centroId: initial?.centroId ?? null,
      capacidad: initial?.capacidad ?? 1,
      nota: initial?.nota ?? "",
      publicar: mode === "create" ? true : false,
    });
    setErrors({});
  }, [open, initial, mode]);

  const esPresencial = Number(form.formatoId) === Number(PRESENCIAL_ID);

  const handle = (k: keyof typeof form) => (e: any) => {
    let v = e?.target?.value ?? e;
    if (k === "capacidad") v = Number(v);
    if (k === "formatoId") v = Number(v);
    if (k === "centroId") v = v === "" ? null : Number(v);
    setForm((p) => ({ ...p, [k]: v }));
  };

  const hhmmToMinutes = (hhmm: string) => {
    const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
    if (!m) return NaN;
    const h = Number(m[1]),
      min = Number(m[2]);
    if (h > 23 || min > 59) return NaN;
    return h * 60 + min;
  };

  const validate = () => {
    const next: typeof errors = {};
    if (esPresencial && !form.centroId)
      next.centroId = "El centro es obligatorio en formato presencial";

    const startMin = hhmmToMinutes(form.horaInicio);
    const endMin = hhmmToMinutes(form.horaFin);

    if (Number.isNaN(startMin)) next.horaInicio = "Hora inválida (HH:mm)";
    if (Number.isNaN(endMin)) next.horaFin = "Hora inválida (HH:mm)";
    if (!Number.isNaN(startMin) && !Number.isNaN(endMin) && endMin <= startMin) {
      next.horaFin = "La hora fin debe ser posterior a la hora inicio";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.warning("Revisa los campos resaltados");
      return;
    }

    const dto: any = {
      cuadroId: form.cuadroId,
      fecha: form.fecha,
      horaInicio: form.horaInicio,
      horaFin: form.horaFin,
      formatoId: form.formatoId,
      capacidad: Number(form.capacidad) || 1,
      nota: form.nota || undefined,
      ...(esPresencial ? { centroId: form.centroId ?? undefined } : {}),
    };

    try {
      await onSubmit(dto);
      onClose();
    } catch (err: any) {
      // No re-toastear si el interceptor ya lo hizo
      const handled = err && (err as any).__handled;
      const validations = err?.response?.data?.errors;
      if (Array.isArray(validations)) {
        const next: typeof errors = {};
        for (const v of validations) {
          if (v?.field === "centroId") next.centroId = v?.message || "Centro obligatorio";
          if (v?.field === "horaInicio") next.horaInicio = v?.message || "Hora inválida";
          if (v?.field === "horaFin") next.horaFin = v?.message || "Hora inválida";
        }
        setErrors((p) => ({ ...p, ...next }));
      }
      if (!handled && !Array.isArray(validations)) {
        toast.error("No se pudo guardar el turno");
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "create" ? "Nuevo turno" : "Editar turno"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Fecha"
            type="date"
            value={form.fecha}
            onChange={handle("fecha")}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Hora inicio"
              value={form.horaInicio}
              onChange={handle("horaInicio")}
              inputProps={{ pattern: "\\d{2}:\\d{2}" }}
              error={!!errors.horaInicio}
              helperText={errors.horaInicio}
              fullWidth
            />
            <TextField
              label="Hora fin"
              value={form.horaFin}
              onChange={handle("horaFin")}
              inputProps={{ pattern: "\\d{2}:\\d{2}" }}
              error={!!errors.horaFin}
              helperText={errors.horaFin}
              fullWidth
            />
          </Stack>

          <TextField
            label="Formato"
            select
            value={form.formatoId}
            onChange={handle("formatoId")}
            fullWidth
          >
            {formatos.map((f: any) => (
              <MenuItem key={f.id} value={f.id}>
                {f.nombreFormato}
              </MenuItem>
            ))}
          </TextField>

          {esPresencial && (
            <TextField
              label="Centro"
              select
              value={form.centroId ?? ""}
              onChange={handle("centroId")}
              fullWidth
              required
              error={!!errors.centroId}
              helperText={errors.centroId}
            >
              {centros.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nombre}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label="Capacidad"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={form.capacidad}
            onChange={handle("capacidad")}
            fullWidth
          />

          <TextField
            label="Nota (opcional)"
            value={form.nota}
            onChange={handle("nota")}
            fullWidth
            multiline
            minRows={2}
          />

          {mode === "create" && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.publicar}
                  onChange={(_, v) => setForm((p) => ({ ...p, publicar: v }))}
                />
              }
              label="Publicar turno al crear"
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={esPresencial && !form.centroId}>
          {mode === "create" ? "Crear" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
